import React, { useCallback } from "react";
import client from "@culturehq/client";
import { Icon, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { PostLink, ProfileLink } from "app/links";
import { canAdminPost } from "app/permissions";
import { Trans } from "app/utils";

import PostModal from "components/PostModal";
import QuickOptions from "components/display/QuickOptions";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import PinUserActivityToggle from "components/feed/feed-item/PinUserActivityToggle";
import Flag from "components/common/Flag";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import FeedItemTitle from "./FeedItemTitle";

const PostQuickOptions = ({ userActivity, onUserActivityUpdate, onUserActivityRemove }) => {
  const { profile } = useProfile();
  const { entity: post } = userActivity;

  const onPostEdited = useCallback(
    entity => onUserActivityUpdate({ userActivityId: userActivity.id, updates: { entity } }),
    [userActivity, onUserActivityUpdate]
  );

  const onPostDeleted = async () => {
    await client.deletePost({ postId: userActivity.entity.id });
    onUserActivityRemove(userActivity.id);
  };

  return (
    <QuickOptions>
      <ul className="dropdown-menu">
        <li>
          <PostLink post={post}>
            <Icon icon="eye" />
            <Trans>View this post</Trans>
          </PostLink>
        </li>
        {profile.organizationAdmin && (
          <li>
            <PinUserActivityToggle
              userActivity={userActivity}
              onUserActivityUpdate={onUserActivityUpdate}
            />
          </li>
        )}
        {canAdminPost(profile, post) && (
          <>
            <li>
              <PostModal post={userActivity.entity} onPosted={onPostEdited} />
            </li>
            <li>
              <ChqModalConfirmDelete
                onAccept={onPostDeleted}
                trigger={onTrigger => (
                  <PlainButton onClick={onTrigger} className="link action">
                    <Icon icon="trash-a" />
                    <Trans>Delete</Trans>
                  </PlainButton>
                )}
              >
                <Trans>Are you sure you want to delete this update?</Trans>
              </ChqModalConfirmDelete>
            </li>
          </>
        )}
        <li>
          <Flag action="flagPost" options={{ postId: post.id }}>
            {onTrigger => (
              <PlainButton onClick={onTrigger}>
                <Icon icon="ios-flag" />
                <Trans>Flag post as inappropriate</Trans>
              </PlainButton>
            )}
          </Flag>
        </li>
      </ul>
    </QuickOptions>
  );
};

const PostCreated = ({ userActivity, onUserActivityUpdate, onUserActivityRemove }) => {
  const { entity: post } = userActivity;

  return (
    <>
      <FeedItemTitle item={userActivity} date={userActivity.createdAt}>
        <PostQuickOptions
          userActivity={userActivity}
          onUserActivityUpdate={onUserActivityUpdate}
          onUserActivityRemove={onUserActivityRemove}
        />
        <Trans>
          <ProfileLink user={post.creator} />
          {" "}
          posted an update
        </Trans>
      </FeedItemTitle>
      <h3 className="FeedItem__preview__title">{post.title}</h3>
      <LazyEditorOutput output={post.body} />
    </>
  );
};

export default PostCreated;
